import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, API, scaricaFile } from "@/lib/api";
import { num, dateIt } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, FileDown, Truck } from "lucide-react";

const isoLocale = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function RiepilogoGiornaliero() {
  const [data, setData] = useState(isoLocale(new Date()));
  const { data: rep, isLoading } = useQuery({
    queryKey: ["riepilogo-giornaliero", data],
    queryFn: async () => (await api.get("/report/riepilogo-giornaliero", { params: { data } })).data,
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(data),
  });
  const righe = rep?.righe || [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="riepilogo-giornaliero">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-base md:text-lg font-semibold text-slate-900 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Riepilogo giornaliero</h2>
          <p className="text-xs text-slate-500 mt-0.5">Dipendenti attivi, commesse, assenze e veicoli per la data scelta.</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col">
            <label className="text-xs text-slate-400 mb-1">Data</label>
            <Input type="date" value={data} onChange={(e) => setData(e.target.value)} className="w-[170px]" data-testid="riepilogo-giornaliero-data" />
          </div>
          <Button variant="outline" onClick={() => scaricaFile(`${API}/report/riepilogo-giornaliero/pdf?data=${data}`, (m) => toast.error(m))} disabled={!rep} data-testid="riepilogo-giornaliero-pdf">
            <FileDown className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>
      {isLoading ? (
        <div className="py-8 text-center text-slate-400">Caricamento…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="riepilogo-giornaliero-tabella">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-3 py-2">Dipendente</th>
                <th className="px-3 py-2">Commessa / Assenza</th>
                <th className="px-3 py-2 text-right">Ore</th>
                <th className="px-3 py-2">Veicoli</th>
              </tr>
            </thead>
            <tbody>
              {righe.map((r) => (
                r.voci.length === 0 ? (
                  <tr key={r.dipendente_id} className="border-b border-slate-50" data-testid={`rg-row-${r.codice}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.nome}</td>
                    <td className="px-3 py-2 text-slate-400 italic" colSpan={3} data-testid={`rg-stato-${r.codice}`}>Non assegnato</td>
                  </tr>
                ) : r.voci.map((v, i) => (
                  <tr key={`${r.dipendente_id}-${i}`} className="border-b border-slate-50" data-testid={`rg-row-${r.codice}-${i}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{i === 0 ? r.nome : ""}</td>
                    <td className="px-3 py-2">
                      {v.tipo === "assenza" ? (
                        <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100" data-testid={`rg-stato-${r.codice}`}>{v.codice}</Badge>
                      ) : (
                        <span data-testid={`rg-stato-${r.codice}`}>{v.tipo === "prestazione" ? <Badge className="bg-orange-100 text-primary hover:bg-orange-100 mr-2">Prestazione</Badge> : <span className="font-mono text-xs text-slate-500 mr-2">{v.codice}</span>}{v.descrizione}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{num(v.ore, 2)} h</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {v.veicoli.length > 0 && <span className="inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-slate-400" />{v.veicoli.join(", ")}</span>}
                    </td>
                  </tr>
                ))
              ))}
              {righe.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Nessun dipendente attivo.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-xs text-slate-400 mt-2">{rep ? `${righe.filter((r) => r.voci.length > 0).length}/${righe.length} dipendenti con registrazioni il ${dateIt(data)}` : ""}</div>

      {rep && (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="riepilogo-veicoli">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Truck className="h-4 w-4 text-primary" /> Veicoli</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(rep.veicoli || []).map((v) => (
              <div key={v.veicolo_id} className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-2 ${v.impegnato ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid={`rg-veicolo-${v.targa}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 text-sm"><span className="font-mono">{v.targa}</span> · {v.modello}</div>
                  {v.impegnato && <div className="text-xs text-slate-600 mt-0.5 truncate">{v.commesse.map((c) => `${c.codice}${c.descrizione ? ` — ${c.descrizione}` : ""}`).join("; ")}</div>}
                </div>
                <Badge className={`shrink-0 ${v.impegnato ? "bg-amber-600 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-600"} text-white`} data-testid={`rg-veicolo-stato-${v.targa}`}>{v.impegnato ? "Impegnato" : "Disponibile"}</Badge>
              </div>
            ))}
            {(rep.veicoli || []).length === 0 && <div className="text-xs text-slate-400">Nessun veicolo in anagrafica.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
